import { useContext } from "react";
import WebsocketWrapperContext from "../../../context/WebsocketWrapperContext";
import { useBuilderContext, useOptionChainScreenContext } from "../context/OptionChainContext";
import { format } from "date-fns";
import { useOptionChainContext } from "../../../context/OptionChainContext";

export const useApplyPreBuiltStrategy = () => {
    const {
        selectedSymbol,
        currentOptionsData,
        selectedExpiry
    } = useOptionChainScreenContext();

    const {
        checkWebsocketConnection,
        subscribeToSymbols,
        unsubscribeToSymbols,
        symbolList,
        symbolData,
    } = useContext(WebsocketWrapperContext);

    function normalizeExpiryDate(dateStr) {
        const parsed = new Date(dateStr);
        return format(parsed, "dd MMM yyyy");  // Outputs "08 May 2025"
    }

    function applyPreBuiltStrategy(strategyName) {
        const atmIndex = returnAtmIndex();
        let childData = [];
        // setSelectedStrategy(strategyName);

        if (!currentOptionsData || !currentOptionsData[selectedExpiry]?.options) {
            console.warn("Option data not available");
            return;
        }

        switch (strategyName) {
            case "buy_call":
                childData = constructInstruments(["Call"], ["Buy"], [atmIndex], [1]);
                break;
            case "sell_put":
                childData = constructInstruments(["Put"], ["Sell"], [atmIndex], [1]);
                break;
            case "bull_call_spread":
                childData = constructInstruments(
                    ["Call", "Call"],
                    ["Buy", "Sell"],
                    [atmIndex + 2, atmIndex + 5],
                    [1, 1]
                );
                break;
            case "bull_put_spread":
                childData = constructInstruments(
                    ["Put", "Put"],
                    ["Buy", "Sell"],
                    [atmIndex - 6, atmIndex - 1],
                    [1, 1]
                );
                break;
            case "bull_butterfly":
                childData = constructInstruments(
                    ["Call", "Call", "Call"],
                    ["Buy", "Sell", "Buy"],
                    [atmIndex + 5, atmIndex + 10, atmIndex + 15],
                    [1, 2, 1]
                );
                break;
            case "bull_condor":
                childData = constructInstruments(
                    ["Call", "Call", "Call", "Call"],
                    ["Buy", "Sell", "Sell", "Buy"],
                    [atmIndex + 5, atmIndex + 10, atmIndex + 15, atmIndex + 20],
                    [1, 1, 1, 1]
                );
                break;
            case "call_ratio_back_spread":
                childData = constructInstruments(
                    ["Call", "Call"],
                    ["Buy", "Sell"],
                    [atmIndex + 3, atmIndex - 2],
                    [2, 1]
                );
                break;
            case "buy_put":
                childData = constructInstruments(["Put"], ["Buy"], [atmIndex], [1]);
                break;
            case "sell_call":
                childData = constructInstruments(["Call"], ["Sell"], [atmIndex], [1]);
                break;
            case "bear_put_spread":
                childData = constructInstruments(
                    ["Put", "Put"],
                    ["Sell", "Buy"],
                    [atmIndex + 2, atmIndex + 5],
                    [1, 1]
                );
                break;
            case "bear_call_spread":
                childData = constructInstruments(
                    ["Call", "Call"],
                    ["Sell", "Buy"],
                    [atmIndex, atmIndex + 4],
                    [1, 1]
                );
                break;
            case "bear_butterfly":
                childData = constructInstruments(
                    ["Put", "Put", "Put"],
                    ["Buy", "Sell", "Buy"],
                    [atmIndex - 5, atmIndex - 10, atmIndex - 15],
                    [1, 2, 1]
                );
                break;
            case "bear_condor":
                childData = constructInstruments(
                    ["Put", "Put", "Put", "Put"],
                    ["Buy", "Sell", "Sell", "Buy"],
                    [atmIndex - 5, atmIndex - 10, atmIndex - 15, atmIndex - 20],
                    [1, 1, 1, 1]
                );
                break;
            case "put_ratio_back_spread":
                childData = constructInstruments(
                    ["Put", "Put"],
                    ["Buy", "Sell"],
                    [atmIndex - 2, atmIndex + 3],
                    [2, 1]
                );
                break;
            case "short_straddle":
                childData = constructInstruments(
                    ["Call", "Put"],
                    ["Sell", "Sell"],
                    [atmIndex, atmIndex],
                    [1, 1]
                );
                break;
            case "short_strangle":
                childData = constructInstruments(
                    ["Call", "Put"],
                    ["Sell", "Sell"],
                    [atmIndex + 8, atmIndex - 8],
                    [1, 1]
                );
                break;
            case "iron_butterfly":
                childData = constructInstruments(
                    ["Call", "Put", "Call", "Put"],
                    ["Buy", "Buy", "Sell", "Sell"],
                    [atmIndex + 5, atmIndex - 5, atmIndex, atmIndex],
                    [1, 1, 1, 1]
                );
                break;
            case "iron_condor":
                childData = constructInstruments(
                    ["Call", "Put", "Call", "Put"],
                    ["Buy", "Buy", "Sell", "Sell"],
                    [atmIndex + 9, atmIndex - 9, atmIndex + 5, atmIndex - 5],
                    [1, 1, 1, 1]
                );
                break;
            case "batman":
                childData = constructInstruments(
                    ["Call", "Call", "Put", "Put"],
                    ["Buy", "Sell", "Buy", "Sell"],
                    [atmIndex + 5, atmIndex + 6, atmIndex - 5, atmIndex - 6],
                    [1, 2, 1, 2]
                );
                break;
            case "jade_lizard":
                childData = constructInstruments(
                    ["Call", "Call", "Put"],
                    ["Sell", "Buy", "Sell"],
                    [atmIndex + 9, atmIndex + 13, atmIndex - 5],
                    [1, 1, 1]
                );
                break;
            case "double_plateau":
                childData = constructInstruments(
                    ["Call", "Call", "Call", "Call", "Put", "Put", "Put", "Put"],
                    ["Buy", "Sell", "Sell", "Buy", "Buy", "Sell", "Sell", "Buy"],
                    [
                        atmIndex + 5,
                        atmIndex + 10,
                        atmIndex + 15,
                        atmIndex + 20,
                        atmIndex - 4,
                        atmIndex - 9,
                        atmIndex - 14,
                        atmIndex - 19,
                    ],
                    [1, 1, 1, 1, 1, 1, 1, 1]
                );
                break;
            case "reverse_jade_lizard":
                childData = constructInstruments(
                    ["Put", "Put", "Call"],
                    ["Sell", "Buy", "Sell"],
                    [atmIndex - 9, atmIndex - 12, atmIndex + 5],
                    [1, 1, 1]
                );
                break;
            case "long_calendar_calls":
                childData = handleCalendarStrategy("Call", atmIndex + 5);
                break;
            case "long_calendar_puts":
                childData = handleCalendarStrategy("Put", atmIndex - 4);
                break;
            default:
                console.warn(`Strategy ${strategyName} not recognized`);
                return;
        }
        console.log("childData", childData)
        return childData;
        // handleCallback([childData]);
    }
    function handleCalendarStrategy(optionType, strikeIndex) {
        try {
            // Parse the current expiry date
            const currentDate = new Date(selectedExpiry);
            const currentMonth = currentDate.getMonth();
            const currentYear = currentDate.getFullYear();

            // Get all expiries as Date objects
            const allExpiries = selectedSymbol.option_expiries.map(
                (exp) => new Date(exp)
            );

            // current month
            const currentMonthExpiries = allExpiries.filter(
                (date) =>
                    date.getMonth() === currentMonth && date.getFullYear() === currentYear
            );

            // Find the last expiry of the current month
            let lastExpiryOfCurrentMonth = null;
            if (currentMonthExpiries.length > 0) {
                lastExpiryOfCurrentMonth = new Date(
                    Math.max(...currentMonthExpiries.map((d) => d.getTime()))
                );
            }

            let nextExpiry;

            // Check if there's a suitable expiry later in the current month
            if (
                lastExpiryOfCurrentMonth &&
                (lastExpiryOfCurrentMonth.getTime() - currentDate.getTime()) /
                (1000 * 60 * 60 * 24) >=
                14
            ) {
                nextExpiry = lastExpiryOfCurrentMonth;
            } else {
                // Look for expiries in the next month
                const nextMonth = (currentMonth + 1) % 12;
                const nextYear = nextMonth === 0 ? currentYear + 1 : currentYear;

                const nextMonthExpiries = allExpiries.filter(
                    (date) =>
                        date.getMonth() === nextMonth && date.getFullYear() === nextYear
                );

                if (nextMonthExpiries.length > 0) {
                    nextExpiry = new Date(
                        Math.max(...nextMonthExpiries.map((d) => d.getTime()))
                    );
                } else {
                    throw new Error("No valid expiry dates found in the next month");
                }
            }
            const formatDate = (date) => {
                return date.toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                });
            };

            const currentExpiryFormatted = formatDate(currentDate);
            const nextExpiryFormatted = formatDate(nextExpiry);

            return constructCalendarInstruments(
                [optionType, optionType],
                ["Sell", "Buy"],
                [strikeIndex, strikeIndex],
                [1, 1],
                [currentExpiryFormatted, nextExpiryFormatted]
            );
        } catch (error) {
            console.error("Error in calendar strategy:", error);
            return [];
        }
    }
    function constructCalendarInstruments(
        call_put_array,
        buy_sell_array,
        index_array,
        quantity_array,
        expiry_dates
    ) {
        let row_list = [];
        let strike = "";

        const expiryDataMap = {};
        for (const expiryDate of expiry_dates) {
            const formattedExpiry = normalizeExpiryDate(expiryDate);
            const data = currentOptionsData[formattedExpiry];

            if (!data) {
                console.error(`No option data found for expiry ${expiryDate}`);
                return [];
            }

            expiryDataMap[expiryDate] = data;
        }

        for (let index = 0; index < call_put_array.length; index++) {
            const expiryDate = expiry_dates[index];
            const expiryData = expiryDataMap[expiryDate];
            const side = call_put_array[index];
            let option;

            // For the first leg, determine strike
            if (index === 0 || strike === "") {
                const atmIndex = index_array[index];
                option =
                    side === "Call"
                        ? expiryData.options[atmIndex].call
                        : expiryData.options[atmIndex].put;

                strike = option.strike;
            } else {
                // For subsequent legs, find the option with matching strike
                const matchingOption = expiryData.options.find((opt) => {
                    const sideOption = side === "Call" ? opt.call : opt.put;
                    return sideOption.strike === strike;
                });

                if (!matchingOption) {
                    console.error(
                        `No matching option found with strike ${strike} for expiry ${expiryDate}`
                    );
                    return [];
                }

                option = side === "Call" ? matchingOption.call : matchingOption.put;
            }

            row_list.push({
                buy_sell: buy_sell_array[index],
                code: option.code,
                expiryDate: option.expiry_date,
                instrumentName: option.instrument_name,
                lotSize: option.lot_size,
                side: side,
                tradingSymbol: option.trading_symbol,
                quantity: quantity_array[index],
                strike: option.strike,
                freeze_qty: option.freeze_qty,
                exchange: option.exchange,
                lot_multiplier: parseFloat(option.lot_size),
            });
        }

        return row_list;
    }
    function constructInstruments(
        call_put_array,
        buy_sell_array,
        index_array,
        quantity_array
    ) {
        let row_list = [];

        for (let index = 0; index < call_put_array.length; index++) {
            let atmIndex = index_array[index];

            // Safety check for index
            if (atmIndex < 0) atmIndex = 0;
            if (atmIndex >= currentOptionsData[selectedExpiry].options?.length)
                atmIndex = currentOptionsData[selectedExpiry].options?.length - 1;

            const side = call_put_array[index];
            const option =
                side === "Call"
                    ? currentOptionsData[selectedExpiry].options[atmIndex].call
                    : currentOptionsData[selectedExpiry].options[atmIndex].put;

            row_list.push({
                buy_sell: buy_sell_array[index],
                code: option.code,
                expiryDate: option.expiry_date,
                instrumentName: option.instrument_name,
                lotSize: option.lot_size,
                side: side,
                tradingSymbol: option.trading_symbol,
                quantity: quantity_array[index],
                strike: option.strike,
                freeze_qty: option.freeze_qty,
                exchange: option.exchange,
                lot_multiplier: parseFloat(option.lot_size || currentOptionsData[selectedExpiry].lot_size),
            });
        }

        return row_list;
    }
    function returnAtmIndex() {
        let index = 0;
        const ltp = symbolData[selectedSymbol?.code]?.ltp || 0;

        if (currentOptionsData[selectedExpiry] && currentOptionsData[selectedExpiry].options && currentOptionsData[selectedExpiry].options?.length > 0) {
            let optionData = currentOptionsData[selectedExpiry].options;
            let curr = parseFloat(optionData[0].strike);
            let diff = Math.abs(ltp - curr);

            for (let val = 0; val < optionData.length; val++) {
                const strike = parseFloat(optionData[val].strike);
                const newDiff = Math.abs(ltp - strike);

                if (newDiff < diff) {
                    diff = newDiff;
                    curr = strike;
                    index = val;
                }
            }
        }

        return index;
    }
    return { applyPreBuiltStrategy };
}